"use client";

import * as React from "react";

const BRAND_PRIMARY = "#0042da";
const BRAND_HIGHLIGHT = "#1a5fff";
const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_HIGHLIGHT} 100%)`;

type SendBotLogoProps = {
  size?: number;
  variant?: "icon" | "full" | "text";
  color?: "primary" | "white" | "black" | "gradient";
  className?: string;
};

const getSolidColor = (
  color: Exclude<SendBotLogoProps["color"], "gradient">,
) => {
  if (color === "white") return "#ffffff";
  if (color === "black") return "#000000";
  return BRAND_PRIMARY;
};

export function SendBotLogo({
  size = 40,
  variant = "icon",
  color = "primary",
  className = "",
}: SendBotLogoProps) {
  const id = React.useId();
  const gradientId = `sendbot-gradient-${id.replace(/:/g, "")}`;
  const mainColor =
    color === "gradient"
      ? `url(#${gradientId})`
      : getSolidColor(color as Exclude<SendBotLogoProps["color"], "gradient">);
  const textStyle: React.CSSProperties = {
    background: color === "gradient" ? BRAND_GRADIENT : undefined,
    WebkitBackgroundClip: color === "gradient" ? "text" : undefined,
    WebkitTextFillColor: color === "gradient" ? "transparent" : undefined,
    color: color === "gradient" ? undefined : mainColor,
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: `${size * 0.5}px`,
    fontWeight: 800,
    letterSpacing: "-0.03em",
  };

  if (variant === "text") {
    return (
      <div
        className={`sendbot-logo-text ${className}`}
        style={{ display: "inline-block" }}
      >
        <span className="sendbot-text" style={textStyle}>
          SendBot
        </span>
      </div>
    );
  }

  const iconSvg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={BRAND_PRIMARY} />
          <stop offset="100%" stopColor={BRAND_HIGHLIGHT} />
        </linearGradient>
      </defs>
      <rect
        x="13"
        y="13"
        width="74"
        height="68"
        rx="25"
        stroke={mainColor}
        strokeWidth="6"
        fill="none"
        strokeLinejoin="round"
      />
      <path d="M 26 81 L 18 92 L 36 84 Z" fill={mainColor} />
      <path
        d="M 67 33 L 40 33 Q 29 33, 29 41.5 Q 29 47.5, 37 50 L 63 50 Q 71 50, 71 58.5 Q 71 64, 60 65 L 34 65"
        stroke={mainColor}
        strokeWidth="8.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <line
        x1="53"
        y1="33"
        x2="53"
        y2="24"
        stroke={mainColor}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="53" cy="21.5" r="3.5" fill={mainColor} />
      <circle cx="36" cy="41.5" r="7.5" fill={mainColor} />
      <circle
        cx="36"
        cy="41.5"
        r="6.3"
        fill="none"
        stroke={color === "white" ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.08)"}
        strokeWidth={color === "white" ? "1" : "0.6"}
      />
      {color === "white" && (
        <circle cx="36" cy="41.5" r="5.5" fill="rgba(0,0,0,0.75)" />
      )}
      <circle
        cx="38.8"
        cy="38.2"
        r={color === "white" ? "3.5" : "3.2"}
        fill="#fff"
      />
      <circle
        cx="33.5"
        cy="43.8"
        r={color === "white" ? "1.6" : "1.4"}
        fill="#fff"
        opacity={color === "white" ? "0.7" : "0.5"}
      />
      <circle cx="58" cy="41.5" r="7.5" fill={mainColor} />
      <circle
        cx="58"
        cy="41.5"
        r="6.3"
        fill="none"
        stroke={color === "white" ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.08)"}
        strokeWidth={color === "white" ? "1" : "0.6"}
      />
      {color === "white" && (
        <circle cx="58" cy="41.5" r="5.5" fill="rgba(0,0,0,0.75)" />
      )}
      <circle
        cx="60.8"
        cy="38.2"
        r={color === "white" ? "3.5" : "3.2"}
        fill="#fff"
      />
      <circle
        cx="56.5"
        cy="43.8"
        r={color === "white" ? "1.6" : "1.4"}
        fill="#fff"
        opacity={color === "white" ? "0.7" : "0.5"}
      />
    </svg>
  );

  if (variant === "icon") return iconSvg;

  return (
    <div
      className={`sendbot-logo-full ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: `${size * 0.3}px`,
      }}
    >
      {iconSvg}
      <span className="sendbot-logo-full-text" style={textStyle}>
        SendBot
      </span>
    </div>
  );
}

export function SendBotLogoBadge({
  size = 80,
  variant = "rounded",
  className = "",
}: {
  size?: number;
  variant?: "rounded" | "square" | "circle";
  className?: string;
}) {
  const id = React.useId();
  const gradientId = `badge-gradient-${id.replace(/:/g, "")}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={BRAND_PRIMARY} />
          <stop offset="100%" stopColor={BRAND_HIGHLIGHT} />
        </linearGradient>
      </defs>
      <rect
        width="100"
        height="100"
        rx={variant === "circle" ? "50" : variant === "rounded" ? "24" : "12"}
        fill={`url(#${gradientId})`}
      />
      <rect
        x="13"
        y="13"
        width="74"
        height="68"
        rx="25"
        stroke="#fff"
        strokeWidth="6"
        fill="none"
        strokeLinejoin="round"
      />
      <path d="M 26 81 L 18 92 L 36 84 Z" fill="#fff" />
      <path
        d="M 67 33 L 40 33 Q 29 33, 29 41.5 Q 29 47.5, 37 50 L 63 50 Q 71 50, 71 58.5 Q 71 64, 60 65 L 34 65"
        stroke="#fff"
        strokeWidth="8.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <line
        x1="53"
        y1="33"
        x2="53"
        y2="24"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="53" cy="21.5" r="3.5" fill="#fff" />
      <circle cx="36" cy="41.5" r="7.5" fill="#fff" />
      <circle cx="36" cy="41.5" r="5.5" fill="rgba(0,0,0,0.72)" />
      <circle cx="38.8" cy="38.2" r="3.2" fill="#fff" />
      <circle cx="33.5" cy="43.8" r="1.4" fill="#fff" opacity="0.7" />
      <circle cx="58" cy="41.5" r="7.5" fill="#fff" />
      <circle cx="58" cy="41.5" r="5.5" fill="rgba(0,0,0,0.72)" />
      <circle cx="60.8" cy="38.2" r="3.2" fill="#fff" />
      <circle cx="56.5" cy="43.8" r="1.4" fill="#fff" opacity="0.7" />
    </svg>
  );
}
